// src/components/RegionMeasurements.js - USING CSS GRID FOR EXACT LAYOUT

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Paper,
    Button,
    FormControlLabel,
    Checkbox,
    OutlinedInput,
    ListItemText
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

import { 
    LENGTH_MEASUREMENT_OPTIONS,
    UNDERCUT_OPTIONS,
    QUALITY_OPTIONS,
    WIDTH_OPTIONS,
    RIDGE_FORM_OPTIONS
} from './RegionConstants';
import { 
    isMandibleRegion,
    canHaveIncisiveBranch,
    getWidthType,
    getLengthMeasurementOptions 
} from './RegionUtils';

const pathologyCommentOptions = [
    "The extraction socket is not completely healed.",
    "Shows ill-defined periapical radiolucent lesion.",
    "Root canal treated 47, thick, consistent root canal filling material noted within the canal.",
    "Shows deep dental caries involving enamel, dentin and pulp.",
    "Small root stump noted.",
    "Fixed prosthetic crown noted.",
    "Missing crown portion noted.",
    "Root canal treated irt 00, 00 with reduced coronal portion of the teeth.",
    "Cervical abrasion noted."
];

const RegionMeasurements = ({
    regionNumber,
    reportType,
    formData,
    onFormDataChange,
    onPredefinedCommentsChange, // New prop
    croppedImageFile1 = null,
    croppedImageFile2 = null,
    croppedImageUrl1 = null,
    croppedImageUrl2 = null,
    onRemoveImage,
    onRefreshImage,
    onSelectImage,
    disabled = false
}) => {
    
    const [selectedPathologyComments, setSelectedPathologyComments] = useState(formData.predefined_pathology_comments || []);

    // Determine region characteristics
    const isMandible = isMandibleRegion(reportType, regionNumber);
    const canShowIncisiveBranch = isMandible && canHaveIncisiveBranch(regionNumber);
    const widthType = getWidthType(regionNumber);
    const lengthOptions = getLengthMeasurementOptions(reportType);

    useEffect(() => {
        const length = parseFloat(formData.length_crest);
        if (!isNaN(length)) {
            let quality = '___';
            if (length >= 11) {
                quality = 'good';
            } else if (length >= 9 && length <= 10) {
                quality = 'moderate';
            } else if (length >= 6 && length <= 8) {
                quality = 'deficient';
            } else {
                quality = 'severely deficient';
            }
            if (quality !== formData.length_quality) {
                onFormDataChange('length_quality', quality);
            }
        }

        const width = parseFloat(formData.width_crest);
        if (!isNaN(width)) {
            let quality = '___';
            if (width >= 6) {
                quality = 'good';
            } else if (width === 5) {
                quality = 'moderate';
            } else if (width === 4) {
                quality = 'deficient';
            } else if (width < 4) {
                quality = 'severely deficient';
            }
            if (quality !== formData.width_quality) {
                onFormDataChange('width_quality', quality);
            }
        }
    }, [formData.length_crest, formData.width_crest, onFormDataChange, formData.length_quality, formData.width_quality]);

    /**
     * Handle form field changes
     */
    const handleFormChange = (event) => {
        const { name, value, type, checked } = event.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        if (onFormDataChange) {
            onFormDataChange(name, newValue);
        }
    };

    const handlePathologyCommentChange = (event) => {
        const {
            target: { value },
        } = event;
        
        const newSelectedComments = typeof value === 'string' ? value.split(',') : value;
        setSelectedPathologyComments(newSelectedComments);
        if (onPredefinedCommentsChange) {
            onPredefinedCommentsChange('predefined_pathology_comments', newSelectedComments);
        }
    };

    /**
     * Handle image load error - No refresh needed with Base64
     */
    const handleImageError = useCallback((imageNumber) => {
        console.warn(`RegionMeasurements ${regionNumber}: Image ${imageNumber} failed to load`);
    }, [regionNumber]);

    /**
     * Handle successful image load
     */
    const handleImageLoad = useCallback((imageNumber) => {
    }, [regionNumber]);

    /**
     * Generate measurement text preview
     */
    const generateMeasurementText = (measurement, target) => {
        const measurementText = measurement || '_____';
        const targetText = target && target !== '___' ? target : '_____';
        return `The length is around ${measurementText} mm from crest to ${targetText}.`;
    };

    /**
     * Generate width text preview
     */
    const generateWidthText = (width, option) => {
        const widthText = width || '_____';
        if (!option || option === '___') {
            return `The width is around ${widthText} mm close to crest.`;
        }
        return `The width is around ${widthText} mm close to crest and ${option}.`;
    };

    /**
     * Render image section - Fixed 350px width
     */
    const renderImageSection = (imageUrl, imageFile, title, imageNumber, measurementText) => (
        <Box sx={{ width: '350px', flexShrink: 0 }}>
            {/* Image Container */}
            <Box sx={{ position: 'relative', mb: '15px' }}>
                <Box
                    sx={{
                        width: '350px',
                        height: '250px',
                        border: '2px solid black',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'grey.200',
                        position: 'relative'
                    }}
                >
                    {/* Remove button */}
                    {imageFile && onRemoveImage && (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={() => onRemoveImage(imageNumber)}
                            sx={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: 'grey.100' }
                            }}
                            disabled={disabled}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                    
                    {imageUrl && imageFile ? (
                        <img
                            src={imageUrl}
                            alt={title}
                            style={{ 
                                width: '100%', 
                                height: '100%', 
                                objectFit: 'contain'
                            }}
                            onLoad={() => handleImageLoad(imageNumber)}
                        />
                    ) : imageFile ? (
                        <Box sx={{ 
                            textAlign: 'center',
                            color: 'warning.main'
                        }}>
                            <Typography variant="body2">
                                Image file attached
                            </Typography>
                            <Typography variant="caption">
                                Preview loading...
                            </Typography>
                        </Box>
                    ) : (
                        <Typography variant="body2" color="text.secondary">
                            {title}
                        </Typography>
                    )}
                </Box>
                
                {/* Debug info */}
                {imageFile && (
                    <Typography variant="caption" sx={{ color: 'success.main', fontSize: 10 }}>
                        {imageFile.name} {imageFile.size ? `(${Math.round(imageFile.size / 1024)}KB)` : ''}
                        {imageUrl && ` • ${imageUrl.startsWith('blob:') || imageUrl.startsWith('data:') ? 'Valid URL' : 'Invalid URL'}`}
                    </Typography>
                )}
            </Box>

            {/* Select from system button */}
            {onSelectImage && (
                <Button
                    variant={imageFile ? "outlined" : "contained"}
                    fullWidth
                    color={imageFile ? "secondary" : "primary"}
                    onClick={() => onSelectImage(imageNumber)}
                    disabled={disabled}
                    sx={{ mb: '15px' }}
                >
                    {imageFile ? 'Replace Image from System' : 'Select Image from System'}
                </Button>
            )}

            {/* Measurement Text */}
            <Box
                sx={{
                    fontSize: '14px',
                    border: '1px solid black',
                    p: '10px'
                }}
            >
                <Typography variant="body2">
                    {measurementText}
                </Typography>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ maxWidth: '1200px', mx: 'auto' }}>
                {/* Pathology Comments Section */}
                <Box sx={{ 
                    border: '2px solid black', 
                    mb: '20px', 
                    p: '20px' 
                }}>
                    <Typography 
                        sx={{ 
                            fontSize: '18px',
                            fontWeight: 'bold',
                            mb: '15px',
                            borderBottom: '1px solid black',
                            pb: '10px'
                        }}
                    >
                        PATHOLOGY COMMENTS
                    </Typography>
                    <textarea
                        name="pathology_comments"
                        placeholder="Enter pathology comments..."
                        value={formData.pathology_comments || ''}
                        onChange={handleFormChange}
                        disabled={disabled}
                        style={{
                            width: '100%',
                            height: '80px',
                            border: '1px solid black',
                            padding: '10px',
                            fontSize: '14px',
                            resize: 'vertical',
                            fontFamily: 'inherit',
                            marginBottom: '15px'
                        }}
                    />
                    <FormControl sx={{ width: '100%' }}>
                        <InputLabel id="pathology-comments-select-label">Select Pathology Comments</InputLabel>
                        <Select
                            labelId="pathology-comments-select-label"
                            id="pathology-comments-select"
                            multiple
                            value={selectedPathologyComments}
                            onChange={handlePathologyCommentChange}
                            input={<OutlinedInput label="Select Pathology Comments" />}
                            renderValue={(selected) => selected.join(', ')}
                            disabled={disabled}
                        >
                            {pathologyCommentOptions.map((comment) => (
                                <MenuItem key={comment} value={comment}>
                                    <Checkbox checked={selectedPathologyComments.indexOf(comment) > -1} />
                                    <ListItemText primary={comment} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
    
                {/* Bone Length Section */}
                <Box sx={{ 
                    border: '2px solid black', 
                    mb: '20px', 
                    p: '20px' 
                }}>
                    <Typography 
                        sx={{ 
                            fontSize: '18px',
                            fontWeight: 'bold',
                            mb: '15px',
                            borderBottom: '1px solid black',
                            pb: '10px'
                        }}
                    >
                        BONE LENGTH
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        gap: '25px',
                        flexDirection: { xs: 'column', lg: 'row' }
                    }}>
                        {/* Left Column - Image Section (350px fixed) */}
                        {renderImageSection(
                            croppedImageUrl1,
                            croppedImageFile1,
                            "Bone Length Image Area",
                            1,
                            generateMeasurementText(formData.length_crest, formData.length_crest_to)
                        )}
    
                        {/* Right Column - Form Section (CSS Grid) */}
                        <Box sx={{ 
                            flex: 1,
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '15px'
                        }}>
                            {/* Row 1: Length from crest */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    Length from crest (mm):
                                </Typography>
                                <input
                                    name="length_crest"
                                    type="number"
                                    placeholder="Enter length here"
                                    value={formData.length_crest || ''}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px'
                                    }}
                                    step="0.1"
                                    min="0"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    To:
                                </Typography>
                                <select
                                    name="length_crest_to"
                                    value={formData.length_crest_to || '___'}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    {lengthOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </Box>
    
                            {/* Row 2: Periapical length */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    (Optional) Length from periapical region/apex of socket (mm):
                                </Typography>
                                <input
                                    name="length_periapical"
                                    type="number"
                                    placeholder="Enter length here"
                                    value={formData.length_periapical || ''}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px'
                                    }}
                                    step="0.1"
                                    min="0"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    To:
                                </Typography>
                                <select
                                    name="length_periapical_to"
                                    value={formData.length_periapical_to || '___'}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    {lengthOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </Box>
    
                            {/* Row 3: Lesion length (Mandible only) */}
                            {isMandible && (
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                        (Optional) Length from the base of the lesion (mm):
                                    </Typography>
                                    <input
                                        name="length_lesion"
                                        type="number"
                                        placeholder="Enter length here"
                                        value={formData.length_lesion || ''}
                                        onChange={handleFormChange}
                                        disabled={disabled}
                                        style={{
                                            border: '1px solid black',
                                            padding: '8px',
                                            fontSize: '14px'
                                        }}
                                        step="0.1"
                                        min="0"
                                    />
                                </Box>
                            )}
    
                            {/* Row 4: Incisive branch (Specific mandible regions) */}
                            {canShowIncisiveBranch && (
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                        (Optional) Length from the crest to the incisive branch (mm):
                                    </Typography>
                                    <input
                                        name="length_incision"
                                        type="number"
                                        placeholder="Enter length here"
                                        value={formData.length_incision || ''}
                                        onChange={handleFormChange}
                                        disabled={disabled}
                                        style={{
                                            border: '1px solid black',
                                            padding: '8px',
                                            fontSize: '14px'
                                        }}
                                        step="0.1"
                                        min="0"
                                    />
                                </Box>
                            )}
    
                            {/* Row 5: Ridge form */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    The ridge is:
                                </Typography>
                                <select
                                    name="ridge_form"
                                    value={formData.ridge_form || '___'}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    {RIDGE_FORM_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </Box>
    
                            {/* Row 6: Bone length quality */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    Bone length is:
                                </Typography>
                                <select
                                    name="length_quality"
                                    value={formData.length_quality || '___'}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    {QUALITY_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </Box>
    
                            {/* Row 7: Undercut (Mandible only) */}
                            {isMandible && (
                                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                        Undercut is:
                                    </Typography>
                                    <select
                                        name="undercut"
                                        value={formData.undercut || '___'}
                                        onChange={handleFormChange}
                                        disabled={disabled}
                                        style={{
                                            border: '1px solid black',
                                            padding: '8px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        {UNDERCUT_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))
                                        }
                                    </select>
                                </Box>
                            )}
    
                            {/* Row 8: Additional comments - Full width */}
                            <Box sx={{ 
                                gridColumn: '1 / -1', 
                                display: 'flex', 
                                flexDirection: 'column' 
                            }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    Any additional comments:
                                </Typography>
                                <textarea
                                    name="additional_comments"
                                    placeholder="Enter/Modify here"
                                    value={formData.additional_comments || ''}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        height: '60px',
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px',
                                        resize: 'vertical',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
    
                {/* Bone Width Section */}
                <Box sx={{ 
                    border: '2px solid black', 
                    mb: '20px', 
                    p: '20px' 
                }}>
                    <Typography 
                        sx={{ 
                            fontSize: '18px',
                            fontWeight: 'bold',
                            mb: '15px',
                            borderBottom: '1px solid black',
                            pb: '10px'
                        }}
                    >
                        BONE WIDTH
                    </Typography>
                    
                    <Box sx={{ 
                        display: 'flex', 
                        gap: '25px',
                        flexDirection: { xs: 'column', lg: 'row' }
                    }}>
                        {/* Left Column - Image Section (350px fixed) */}
                        {renderImageSection(
                            croppedImageUrl2,
                            croppedImageFile2,
                            "Bone Width Image Area",
                            2,
                            generateWidthText(formData.width_crest, formData.width_option)
                        )}
    
                        {/* Right Column - Form Section (CSS Grid) */}
                        <Box sx={{ 
                            flex: 1,
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '15px'
                        }}>
                            {/* Row 1: Width measurement */}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    Width is around (mm):
                                </Typography>
                                <input
                                    name="width_crest"
                                    type="number"
                                    placeholder="Enter width here"
                                    value={formData.width_crest || ''}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px'
                                    }}
                                    step="0.1"
                                    min="0"
                                />
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    and (Optional):
                                </Typography>
                                <select
                                    name="width_option"
                                    value={formData.width_option || '___'}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    {WIDTH_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </Box>
    
                            {/* Row 2: Width quality - Full width */}
                            <Box sx={{ 
                                gridColumn: '1 / -1', 
                                display: 'flex', 
                                flexDirection: 'column' 
                            }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    {`${widthType} width is:`}
                                </Typography>
                                <select
                                    name="width_quality"
                                    value={formData.width_quality || '___'}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px'
                                    }}
                                >
                                    {QUALITY_OPTIONS.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </Box>

                            {/* New Checkbox */}
                            <Box sx={{ gridColumn: '1 / -1' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={formData.width_apical_quality || false}
                                            onChange={handleFormChange}
                                            name="width_apical_quality"
                                            disabled={disabled}
                                        />
                                    }
                                    label="till 6mm and is good as we go apically"
                                />
                            </Box>
    
                            {/* Row 3: Additional comments - Full width */}
                            <Box sx={{ 
                                gridColumn: '1 / -1', 
                                display: 'flex', 
                                flexDirection: 'column' 
                            }}>
                                <Typography sx={{ fontSize: '13px', mb: '5px' }}>
                                    Any additional comments:
                                </Typography>
                                <textarea
                                    name="width_additional_comments"
                                    placeholder="Enter/Modify here"
                                    value={formData.width_additional_comments || ''}
                                    onChange={handleFormChange}
                                    disabled={disabled}
                                    style={{
                                        height: '60px',
                                        border: '1px solid black',
                                        padding: '8px',
                                        fontSize: '14px',
                                        resize: 'vertical',
                                        fontFamily: 'inherit'
                                    }}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Box>
    
                {/* Form Completion Summary */}
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Measurements Summary:
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                            Length Image: {croppedImageFile1 ? '✓' : '○'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Width Image: {croppedImageFile2 ? '✓' : '○'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Length Value: {formData.length_crest ? '✓' : '○'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Width Value: {formData.width_crest ? '✓' : '○'}
                        </Typography>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                        Completion: {[
                            formData.pathology_comments?.trim(),
                            formData.length_crest?.trim(),
                            formData.width_crest?.trim(),
                            formData.length_quality !== '___' ? formData.length_quality : null,
                            formData.width_quality !== '___' ? formData.width_quality : null
                        ].filter(Boolean).length} of 5 key fields completed
                    </Typography>
                </Box>
            </Box>
    );
};

RegionMeasurements.propTypes = {
    regionNumber: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]).isRequired,
    reportType: PropTypes.string.isRequired,
    formData: PropTypes.shape({
        pathology_comments: PropTypes.string,
        predefined_pathology_comments: PropTypes.arrayOf(PropTypes.string), // New prop type
        length_crest: PropTypes.string,
        length_crest_to: PropTypes.string,
        length_periapical: PropTypes.string,
        length_periapical_to: PropTypes.string,
        length_lesion: PropTypes.string,
        length_incision: PropTypes.string,
        length_quality: PropTypes.string,
        ridge_form: PropTypes.string,
        undercut: PropTypes.string,
        additional_comments: PropTypes.string,
        width_crest: PropTypes.string,
        width_option: PropTypes.string,
        width_quality: PropTypes.string,
        width_additional_comments: PropTypes.string,
        width_apical_quality: PropTypes.bool
    }).isRequired,
    onFormDataChange: PropTypes.func.isRequired,
    onPredefinedCommentsChange: PropTypes.func.isRequired, // New prop type
    croppedImageFile1: PropTypes.object,
    croppedImageFile2: PropTypes.object,
    croppedImageUrl1: PropTypes.string,
    croppedImageUrl2: PropTypes.string,
    onRemoveImage: PropTypes.func,
    onRefreshImage: PropTypes.func,
    onSelectImage: PropTypes.func,
    disabled: PropTypes.bool
};

export default RegionMeasurements;
